import type { CSSProperties, ReactNode } from 'react';
import styles from './OptionRow.module.css';

type OptionRowProps = {
  /** Main label (bold) */
  label: ReactNode;
  /** Optional sub-text beneath the label */
  sub?: ReactNode;
  /** Whether this row is currently selected */
  selected: boolean;
  /** Click handler — typically toggles selection in parent state */
  onClick: () => void;
  /** Accent color for selected state. Pass a CSS custom property
   *  (e.g. 'var(--c-training)') or a hex string. Defaults to marine. */
  accent?: string;
  /** Soft variant of the accent used for the selected-state background.
   *  Defaults to --signal-soft. */
  accentSoft?: string;
  /** Optional aria-label override. Falls back to `label` if string. */
  'aria-label'?: string;
};

/**
 * Full-width selectable row used across onboarding steps for journey
 * selection, discipline, time goal type, frequency, group type, etc.
 *
 * Behavior matches Claude Design's mockup OptionRow (see
 * `.claude/design-artifacts/2026-04-21-onboarding-landing/Onboarding Flow v3.html`
 * line ~54): tinted bg + 2px border + filled checkmark circle on the
 * right when selected.
 *
 * Accent defaults to `--signal` (marine). Health motivation chips and
 * other coach-specific accents override via the `accent` prop.
 */
export function OptionRow({
  label,
  sub,
  selected,
  onClick,
  accent,
  accentSoft,
  'aria-label': ariaLabel,
}: OptionRowProps) {
  const style: CSSProperties & Record<string, string> = {};
  if (accent) style['--option-row-accent'] = accent;
  if (accentSoft) style['--option-row-accent-soft'] = accentSoft;

  return (
    <button
      type="button"
      className={[styles.row, selected && styles.selected].filter(Boolean).join(' ')}
      onClick={onClick}
      style={style}
      aria-pressed={selected}
      aria-label={ariaLabel}
    >
      <div className={styles.text}>
        <div className={styles.label}>{label}</div>
        {sub && <div className={styles.sub}>{sub}</div>}
      </div>
      <div className={styles.check} aria-hidden>
        {selected && (
          <svg
            className={styles.checkmark}
            viewBox="0 0 9 9"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.5 4.5l2 2 4-4"
              stroke="#fff"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
    </button>
  );
}
